import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { RefreshCw, X, Loader2 } from 'lucide-react';
import { usePWAContext } from '@/contexts/PWAContext';
import { useState, useEffect, useRef } from 'react';

export function UpdatePrompt() {
  const { updateAvailable, updateApp } = usePWAContext();
  const [dismissed, setDismissed] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await updateApp();
    } finally {
      // Safety net: if reload hasn't happened in 5s, re-enable the button.
      setTimeout(() => setIsUpdating(false), 5000);
    }
  };

  // Re-show the prompt 5 minutes after dismissal
  useEffect(() => {
    if (dismissed && updateAvailable) {
      dismissTimerRef.current = setTimeout(() => {
        setDismissed(false);
      }, 5 * 60 * 1000);
    }
    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
    };
  }, [dismissed, updateAvailable]);

  if (!updateAvailable || dismissed) {
    return null;
  }

  return (
    <div className="fixed top-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-md animate-in slide-in-from-top-5 duration-300">
      <Alert className="bg-primary text-primary-foreground border-primary shadow-lg">
        <RefreshCw className="h-4 w-4" />
        <AlertTitle className="flex items-center justify-between">
          <span>Aggiornamento Disponibile</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 hover:bg-primary-foreground/10"
            onClick={() => setDismissed(true)}
          >
            <X className="h-4 w-4" />
          </Button>
        </AlertTitle>
        <AlertDescription className="mt-2">
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm opacity-90">
              È disponibile una nuova versione di MaestroCRM.
            </span>
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={handleUpdate}
              disabled={isUpdating}
              className="shrink-0"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Aggiornamento...
                </>
              ) : (
                'Aggiorna'
              )}
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}
